import { jsx as _jsx } from "react/jsx-runtime";
import * as React from "react";
import { cn } from "../lib/utils";
import { RibbonEngine } from "./engine";
/** A plain requestAnimationFrame clock, for hosts without a shared ticker. */
export const rafDriver = {
    subscribe(tick) {
        let handle = 0;
        const loop = (now) => {
            tick(now);
            handle = window.requestAnimationFrame(loop);
        };
        handle = window.requestAnimationFrame(loop);
        return () => window.cancelAnimationFrame(handle);
    },
};
function sceneKey(scene) {
    if (!scene)
        return "none";
    return [
        scene.composition.id,
        scene.opacity,
        ...scene.waypoints.map((w) => `${w.blockId}:${w.role}`),
    ].join("|");
}
/**
 * The ribbon as a React element. Renders the same `<canvas>` on the server and
 * the client (so hydration never mismatches) and brings the engine up in an
 * effect. The engine — and its WebGL context — lives for the whole mount;
 * scene changes reconfigure it in place.
 */
export const RibbonBackground = React.forwardRef(function RibbonBackground({ scene, mode, scroll, root, driver, diagnostics, measureKey, className, onState }, ref) {
    const canvasRef = React.useRef(null);
    const engineRef = React.useRef(null);
    const onStateRef = React.useRef(onState);
    onStateRef.current = onState;
    const sceneRef = React.useRef(scene);
    sceneRef.current = scene;
    const key = sceneKey(scene);
    const diagnosticsOff = diagnostics === null;
    // No scene, no canvas. An `alpha: false` WebGL canvas is opaque black until
    // it is drawn into, and a fixed z-index -1 canvas shows through every
    // transparent surface above it — so a page without a ribbon must not carry
    // one at all. `scene` is known on the server too, so SSR and hydration agree.
    const hasScene = scene !== null;
    React.useImperativeHandle(ref, () => ({
        frame: (nowMs) => engineRef.current?.frame(nowMs),
        measure: () => engineRef.current?.measure(),
    }), []);
    // Engine lifetime = mount lifetime (per mode; a mode change is a rebuild).
    React.useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !hasScene)
            return;
        const engine = new RibbonEngine({
            canvas,
            root: root ? root() : document,
            scroll: scroll(),
            mode,
            diagnostics: diagnosticsOff
                ? null
                : (diagnostics ?? new URLSearchParams(window.location.search)),
            onState: (state) => onStateRef.current?.(state),
        });
        engineRef.current = engine;
        // A (re)built engine always starts from the scene the host currently
        // wants — the scene effect below only fires when that scene changes.
        engine.setScene(sceneRef.current);
        const onVisibility = () => engine.setVisible(document.visibilityState === "visible");
        document.addEventListener("visibilitychange", onVisibility);
        onVisibility();
        const observed = root ? root() : null;
        const resizeObserver = typeof ResizeObserver !== "undefined" && observed instanceof Element
            ? new ResizeObserver(() => {
                engine.measure();
                if (!driver)
                    engine.frame(performance.now());
            })
            : null;
        resizeObserver?.observe(observed);
        const onResize = () => {
            engine.measure();
            if (!driver)
                engine.frame(performance.now());
        };
        window.addEventListener("resize", onResize);
        document.fonts?.ready.then(() => {
            if (engineRef.current === engine)
                engine.measure();
        });
        return () => {
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("resize", onResize);
            resizeObserver?.disconnect();
            engine.destroy();
            if (engineRef.current === engine)
                engineRef.current = null;
        };
        // scroll/root are factories; a mode change rebuilds by design, and the
        // canvas only exists while there is a scene.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, diagnosticsOff, hasScene]);
    // Scene swaps reconfigure the live engine without a context rebuild. (At
    // mount this repeats the engine effect's setScene, which is harmless — the
    // engine sees the same preset and only re-measures.)
    React.useEffect(() => {
        engineRef.current?.setScene(scene);
        // `key` is the stable identity of `scene`; the object itself is recreated
        // by hosts on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);
    // Layout changes (route, canvas width) re-measure; a frame lands the result.
    React.useEffect(() => {
        const engine = engineRef.current;
        if (!engine)
            return;
        // Measure after the new page's DOM has laid out.
        const handle = window.requestAnimationFrame(() => {
            engine.measure();
            engine.frame(performance.now());
        });
        return () => window.cancelAnimationFrame(handle);
    }, [measureKey, key]);
    React.useEffect(() => {
        if (!driver)
            return;
        return driver.subscribe((nowMs) => engineRef.current?.frame(nowMs));
    }, [driver]);
    if (!hasScene)
        return null;
    return (_jsx("canvas", { ref: canvasRef, className: cn("ribbon-canvas", className), "data-ribbon-canvas": "", "aria-hidden": true }));
});
//# sourceMappingURL=RibbonBackground.js.map